import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import HubspotForm from 'react-hubspot-form';
import { useLocale } from '@react-aria/i18n';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useUser } from '@utmb/contexts/AuthContext';
import { LoyaltyLevelEnum } from '@utmb/types/enum/LoyaltyLevelEnum';
import styles from './contact-form.module.scss';

interface HubspotContactFormProps {
    level: LoyaltyLevelEnum | null;
}

type PrefillableField = 'email' | 'country_of_residence__c' | 'firstname' | 'lastname';

type PrefillValues = Record<PrefillableField, string | undefined> & { language: string };

const HIDE_CLASS_BY_FIELD: Record<PrefillableField, string> = {
    email: styles.hideEmail,
    country_of_residence__c: styles.hideCountry,
    firstname: styles.hideFirstname,
    lastname: styles.hideLastname,
};

// HubSpot's embed listens to synthetic (non trusted) `input` events on its fields and reads `target.value` from them:
// this is the supported way to set a value programmatically. The native setter keeps React's value tracker in sync.
const setFieldValue = (el: HTMLInputElement | HTMLSelectElement, value: string): boolean => {
    const proto = el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) setter.call(el, value);
    else el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    return el.value === value;
};

// HubSpot re-renders the form a few times after init (captcha, dependent fields…), so the prefill is re-applied.
const PREFILL_RETRY_DELAYS_MS = [0, 500, 1500];

export const HubspotContactForm = ({ level }: HubspotContactFormProps): ReactElement => {
    const { locale } = useLocale();
    const { dashboardMember } = useDashboard();
    const { me } = useUser();

    const wrapperRef = useRef<HTMLDivElement>(null);
    const timersRef = useRef<number[]>([]);
    const [hiddenFields, setHiddenFields] = useState<PrefillableField[]>([]);

    // The HubSpot callbacks are registered once at mount: read the latest member data through a ref.
    const valuesRef = useRef<PrefillValues>({ email: undefined, country_of_residence__c: undefined, firstname: undefined, lastname: undefined, language: '' });
    valuesRef.current = {
        email: me?.email,
        country_of_residence__c: dashboardMember?.information?.countryCode,
        firstname: dashboardMember?.information?.firstName,
        lastname: dashboardMember?.information?.lastName,
        language: locale.toUpperCase(),
    };

    let formId = '0b4cf687-56ad-4d4d-b23e-cfa0846dfa5d';
    if (locale === 'fr') formId = '6d412699-5cbf-4971-90b6-1831e20b58ed';
    else if (locale === 'es') formId = '54cbba84-0187-4645-8f93-5b1876638a5e';

    if (level === LoyaltyLevelEnum.LEVEL3) {
        formId = '7efeb24c-ce0e-42cc-a662-5520e08c3b42';
        if (locale === 'fr') formId = '8f41b3fe-e53b-4229-b114-d5fb5c088485';
        else if (locale === 'es') formId = '156b68a1-e45e-40eb-9c32-6651f6f99b64';
        else if (locale === 'it') formId = 'e771bb5e-2914-459a-912a-367d6135cb4b';
    }

    const prefill = useCallback((): void => {
        const form = wrapperRef.current?.querySelector('form.hs-form-private');
        if (!form) return;

        const { language, ...values } = valuesRef.current;
        const filled: PrefillableField[] = [];
        (Object.keys(values) as PrefillableField[]).forEach((name) => {
            const value = values[name];
            const field = form.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);
            if (!value || !field) return;
            if (field.value === value || setFieldValue(field, value)) filled.push(name);
        });

        const languageField = form.querySelector<HTMLInputElement>('[name="TICKET.language"]');
        if (languageField && languageField.value !== language) setFieldValue(languageField, language);

        // Only hide a field once it holds a value, otherwise the member could not submit the form.
        setHiddenFields((prev) => (prev.length === filled.length && prev.every((f) => filled.includes(f)) ? prev : filled));
    }, []);

    const schedulePrefill = useCallback((): void => {
        timersRef.current.forEach((t) => window.clearTimeout(t));
        timersRef.current = PREFILL_RETRY_DELAYS_MS.map((delay) => window.setTimeout(prefill, delay));
    }, [prefill]);

    // Member data may arrive after the form is ready
    useEffect(() => {
        prefill();
    }, [prefill, me?.email, dashboardMember?.information, locale]);

    useEffect(() => {
        return () => timersRef.current.forEach((t) => window.clearTimeout(t));
    }, []);

    return (
        <div id="dedicatedContactForm" ref={wrapperRef} className={[styles.hubspotForm, ...hiddenFields.map((f) => HIDE_CLASS_BY_FIELD[f])].join(' ')}>
            <HubspotForm
                portalId="25932013"
                region="eu1"
                formId={formId}
                // An explicit `css` option makes HubSpot render the form inline instead of inside an iframe "shell",
                // which is required for the page styles to apply and for the prefill below to reach the fields.
                css=""
                loading={<div>Loading Contact form...</div>}
                locale={locale}
                onFormReady={schedulePrefill}
                onReady={schedulePrefill}
            />
        </div>
    );
};
